import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { Adapt } from './lib/adapt.js';
import { SceneClock } from './lib/scene-clock.js';

// Contrat du rig partagé : interdit. Le trou noir est sa propre lumière.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'blackhole',
  family: 'cosmic',
  danceability: 0.18,
  requiresTier: 1,
  energyLevel: 0.10,
  bpmMin: 40,
  bpmMax: 120,
  movement: 0.10,
  tonality: 0.80,
  enabled: true
};

// BLACKHOLE : un trou noir au centre de l'écran (R19), son disque
// d'accrétion incandescent, et la lumière courbée par la gravité : on voit le
// dessus ET le dessous du disque enroulés autour de l'ombre, un fin anneau de
// photons, et les étoiles qui se déforment autour. Ambient, musique spatiale,
// drone, musique de film. Règles R1 à R19, module adapt.js, horloge de scène
// partagée (lib/scene-clock.js, « R20 » des briefs).
//
// LA GÉOMÉTRIE, le détail décisif. Unités : le rayon de l'horizon r_s = 1.
// Pour chaque pixel, le rayon part de la caméra ; sa trajectoire reste dans
// le plan (caméra, trou noir, direction du pixel), de base e1 = caméra / |caméra|
// et e2 = la composante de la direction orthogonale à e1. On y intègre
// l'équation de Binet de Schwarzschild en u = 1/r :
//     u'' = -u + (3/2) u²        (dérivées par rapport à l'angle φ)
// par RK4 à pas d'angle constant, à borne constante (64 pas au tier 2, 32 au
// tier 1, 24 au tier 0). Pourquoi en φ et pas en temps : l'échappement est
// EXACT (u atteint 0 à un angle fini : la direction de sortie est l'angle où
// u s'annule, sans aller loin), et la précision est celle d'une solution
// lisse (0,02 à 0,09 mrad sur la direction de sortie contre une référence
// fine, tools/test-blackhole.mjs). Pendant l'intégration :
//   - u >= 1 : le rayon tombe dans l'horizon -> noir pur (l'ombre) ;
//   - le plan du disque est traversé quand z = r (cos φ e1.z + sin φ e2.z)
//     change de signe -> on lit le disque à ce rayon, le disque est
//     légèrement translucide (alpha <= 0,92), on accumule et on continue ;
//   - u <= 0 : échappé -> le ciel dans la direction cos φ e1 + sin φ e2.
// Les rayons quasi critiques (b à ±0,1 de 3√3/2), qui tournent autour de la
// sphère de photons plus longtemps que le budget, sortent par symétrie de la
// trajectoire autour du périastre : φ_sortie = 2 φ_p + ψ (ψ l'angle initial au
// rayon ; erreur <= 2 mrad). Résultat : l'image arrière du disque passe
// au-dessus ET au-dessous de l'ombre, avec ses images d'ordre supérieur.
//
// R2 : la passe d'intégration est en demi-résolution (tiers au tier 1), en
// deux cibles demi-flottantes (MRT) : la couleur ACCUMULÉE du disque + la
// transmittance, et la DÉVIATION de la direction de sortie par rapport à la
// direction du pixel (petite loin du trou : précise en demi-flottant). La
// composition en pleine résolution lit le ciel dans d + déviation : des
// étoiles nettes d'un pixel (R7), qui se déforment et se dédoublent près de
// l'ombre (anneaux d'Einstein). L'ombre et l'anneau de photons sont tracés en
// PLEINE résolution à partir du paramètre d'impact exact b = D sin(angle au
// centre) : bord net de l'ombre, anneau de 1 à 2 px (R1).
//
// LE DISQUE : de r = 3 (dernière orbite stable) à r = 6, plat ; température
// en (3/r)^0,75 : blanc-jaune au bord intérieur, orange, rouge sombre. Une
// turbulence fbm en coordonnées polaires (étirée selon l'angle, périodique
// en angle : pas de couture) qui TOURNE à la vitesse képlérienne (3/r)^1,5 :
// rotation différentielle. Le cisaillement s'enroulerait sans fin : deux
// couches de phases décalées se relaient en fondu (période 40 unités de phase).
// DOPPLER : vitesse orbitale β = √(0,5 / (r - 1)), facteur g = √(1-β²) / (1 - β
// cos θ) x √(1 - 1/r) (décalage gravitationnel) ; intensité en g³, couleur
// lue à la température g x T : le côté qui vient vers la caméra est plus
// clair et plus bleuté.
//
// CORRECTIONS du 17/10/2026 (retour : ombre grise, anneau pâle, disque avant
// terne, pointillés) :
// - L'OMBRE est noir pur : le bloom partagé, qui étalait un dégradé gris dans
//   l'ombre, est coupé (l'anneau a sa propre lueur, vers l'extérieur seulement) ;
//   la composition interpole la cible basse en ne gardant que les texels du même
//   côté de l'horizon que le pixel (sinon la lumière de l'anneau débordait au
//   tier 1) ; le masque de pochette multiplie (R3).
// - L'ANNEAU DE PHOTONS : les rayons à b dans [b_c, b_c + 0,35] reçoivent des
//   pas supplémentaires (160 / 96 / 64) et un pas d'angle plus fin près de
//   r = 1,5 : ils font plusieurs tours et recueillent le disque à chaque
//   traversée. Leurs images d'ordre supérieur, plus fines qu'un texel bas,
//   sont remplacées dans les 6 px du bord par l'anneau analytique pleine
//   résolution : 1 à 3 px, blanc chaud, au-delà de 0,9.
// - LE DISQUE AVANT : chaque traversée émet à pleine intensité, son opacité
//   n'atténue que ce qui est derrière (avant : émission x opacité) ; vu en
//   rasant, le disque brille davantage (trajet plus long dans la matière).
// - LA TEXTURE : 3 octaves étirées le long de l'orbite (6/12/24 cellules par
//   tour, 10/20/40 par unité de log r), chacune remplacée par sa moyenne
//   au-delà de ce que le pixel peut représenter dans le plan du disque ; bord
//   interne net à la taille du pixel, bord externe qui s'efface.
// - LES ÉTOILES : la démagnification est adoucie (μ^0,3) : l'anneau d'Einstein
//   (étoiles concentrées juste hors du disque) reste visible.
//
// LA MUSIQUE : aucune impulsion (R9). adapt.intensity, lissée sur 8 s, fait
// varier la luminosité du disque et sa vitesse de rotation. La CAMÉRA suit
// l'horloge de scène : de 40 à 25 rayons en 20 s (en partant déjà proche :
// l'ombre et ses arcs occupent le centre dès la première image, champ de 22°), un palier, puis un aller-retour très lent 25 <-> 40
// (2 minutes) si la scène se prolonge. Caméra 10° au-dessus du plan du disque.
//
// Debug : ?bhsteps=<n> (pas d'intégration), ?bhdoppler=0, ?bht=<s> (temps de
// scène fixe : la caméra s'arrête), ?bhprobe=1 (window.__blackhole).

const TIER = {
  0: { scale: 0.25, steps: 24, extra: 64 },
  1: { scale: 1 / 3, steps: 32, extra: 96 },
  2: { scale: 0.5, steps: 64, extra: 160 }
};
const CRIT_BAND = 0.35;                       // b dans [b_c, b_c + 0,35] : les rayons qui frôlent la sphère de photons
const B_CRIT = 3 * Math.sqrt(3) / 2;          // paramètre d'impact critique (r_s = 1)
const R_IN = 3, R_OUT = 6;
const ELEVATION = 10 * Math.PI / 180;
const VFOV = 22 * Math.PI / 180;               // à 25 rayons, les arcs lentillés restent dans le cadre avec ~8 % de marge (R6) ; à 30° la scène perdait sa majesté
const D_FAR = 40, D_NEAR = 25, APPROACH_S = 20, HOLD_S = 15, DRIFT_S = 120;
const PHASE_PERIOD = 40;
const SAFE_FLOOR = 0.55;
const EXPOSURE = 1.0;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function smooth(x) { const t = clamp01(x); return t * t * (3 - 2 * t); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  try { return new URLSearchParams(location.search).get(name); } catch (e) { return null; }
}
// le pas d'angle pour un nombre de pas : 64 -> 0,0875, 32 -> 0,175 (testés)
export function stepAngle(steps) { return 5.6 / Math.max(8, steps); }

// la distance de la caméra au temps de scène s
export function cameraDistance(s) {
  if (s < APPROACH_S) return D_FAR - (D_FAR - D_NEAR) * smooth(s / APPROACH_S);
  if (s < APPROACH_S + HOLD_S) return D_NEAR;
  const u = (s - APPROACH_S - HOLD_S) / DRIFT_S;
  return D_NEAR + (D_FAR - D_NEAR) * (0.5 - 0.5 * Math.cos(u * Math.PI * 2));
}

// L'INTÉGRATEUR, en JavaScript : le même schéma que le shader (testé en Node)
// D distance de la caméra, b paramètre d'impact -> { out: 'capt'|'esc', phiE, crossings: [φ] }
// le pas d'angle : plus fin près de la sphère de photons (u = 2/3, r = 1,5 r_s)
export function adaptiveStep(dphi, u) { const x = Math.abs(u - 2 / 3); const t = Math.max(0, Math.min(1, (x - 0.04) / 0.18)); return dphi * (0.45 + 0.55 * t * t * (3 - 2 * t)); }
export function traceRay(D, b, steps, dphi, extra) {
  const sp = b / D, cp = Math.sqrt(Math.max(0, 1 - sp * sp)), psi = Math.asin(Math.min(1, sp));
  let u = 1 / D, w = u * cp / Math.max(sp, 1e-9), phi = 0, phiP = null, crossings = 0;
  const f = (x) => -x + 1.5 * x * x;
  const near = b > B_CRIT && b < B_CRIT + CRIT_BAND;
  const total = steps + (extra || 0);
  for (let i = 0; i < total; i++) {
    if (i >= steps && !near) break;
    const h = adaptiveStep(dphi, u);
    const k1u = w, k1w = f(u), k2u = w + 0.5 * h * k1w, k2w = f(u + 0.5 * h * k1u);
    const k3u = w + 0.5 * h * k2w, k3w = f(u + 0.5 * h * k2u), k4u = w + h * k3w, k4w = f(u + h * k3u);
    const un = u + h / 6 * (k1u + 2 * k2u + 2 * k3u + k4u), wn = w + h / 6 * (k1w + 2 * k2w + 2 * k3w + k4w);
    if (phiP === null && w > 0 && wn <= 0) phiP = phi + h * w / (w - wn);
    if (Math.floor((phi + h) / Math.PI) !== Math.floor(phi / Math.PI)) crossings++;   // (plan équatorial vu de face : une traversée par demi-tour)
    if (un <= 0) return { out: 'esc', phiE: phi + h * u / (u - un), turns: (phi + h) / (2 * Math.PI) };
    if (un >= 1) return { out: 'capt' };
    u = un; w = wn; phi += h;
  }
  if (b < B_CRIT) return { out: 'capt' };
  return { out: 'esc', phiE: phiP !== null ? 2 * phiP + psi : phi, approx: true };
}

// ------------------------------------------------------------------ GLSL
const TRACE_VERTEX = `
  precision highp float;
  in vec3 position;
  in vec2 uv;
  out vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;

function buildTraceFragment(steps, dphi, extra) {
  return `
  precision highp float;
  uniform vec3 uCam;
  uniform vec3 uRight, uUp, uFwd;
  uniform vec2 uTan;
  uniform float uPix;              // un pixel de la cible basse, en radians
  uniform float uPhaseA, uPhaseB, uMixAB;
  uniform float uBright;
  uniform float uDoppler;
  in vec2 vUv;
  layout(location = 0) out vec4 oDisk;
  layout(location = 1) out vec4 oDir;

  // bruit de valeur à période entière sur l'axe y (l'angle : aucune couture)
  float hash(vec2 p) { vec3 q = fract(vec3(p.xyx) * 0.1031); q += dot(q, q.yzx + 33.33); return fract((q.x + q.y) * q.z); }
  float vnoiseP(vec2 p, float period) {
    vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
    float y0 = mod(i.y, period), y1 = mod(i.y + 1.0, period);
    float a = hash(vec2(i.x, y0)), b = hash(vec2(i.x + 1.0, y0)), c = hash(vec2(i.x, y1)), d = hash(vec2(i.x + 1.0, y1));
    return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
  }
  // une octave de turbulence, FILTRÉE : au-delà de ce que le pixel (fp, en unités de r)
  // peut représenter, elle est remplacée par sa moyenne (0,5) : pas de pointillés
  float octave(float lr, float t, float r, float F, float N, float off, float fp) {
    float cell = min(r / F, 6.2831853 * r / N);     // la plus petite dimension d'une cellule
    float vis = 1.0 - smoothstep(0.5, 1.0, fp / cell);
    return mix(0.5, vnoiseP(vec2(lr * F + off, t * N), N), vis);
  }
  // la turbulence : étirée le long de l'orbite (6, 12, 24 cellules par tour) et plus
  // fine en rayon (10, 20, 40 par unité de log r), trois octaves
  float turb(float r, float ang, float phase, float fp) {
    float a = ang - pow(${R_IN.toFixed(1)} / r, 1.5) * phase;      // rotation différentielle (képlérienne)
    float t = a / 6.2831853, lr = log(r);
    return 0.55 * octave(lr, t, r, 10.0, 6.0, 0.0, fp) + 0.30 * octave(lr, t, r, 20.0, 12.0, 5.2, fp) + 0.15 * octave(lr, t, r, 40.0, 24.0, 1.7, fp);
  }
  // la couleur d'un corps chauffé (température relative : 1 = le bord intérieur)
  vec3 heat(float t) {
    vec3 c = mix(vec3(0.22, 0.02, 0.0), vec3(0.85, 0.16, 0.02), smoothstep(0.30, 0.55, t));
    c = mix(c, vec3(1.0, 0.52, 0.10), smoothstep(0.55, 0.80, t));
    c = mix(c, vec3(1.0, 0.86, 0.60), smoothstep(0.80, 1.05, t));
    // (au-delà : blanc à peine bleuté ; un blanc bleu peu intense se lisait gris sur les images secondaires)
    c = mix(c, vec3(1.0, 0.96, 0.94), smoothstep(1.10, 1.55, t));
    return c;
  }
  // le disque au point P, vu par un photon qui part vers la caméra dans la direction k ;
  // fp : la taille du pixel dans le plan du disque (unités de r)
  vec3 disk(vec3 P, vec3 k, float fp, out float alpha) {
    float r = length(P.xy);
    alpha = 0.0;
    if (r < ${(R_IN * 0.9).toFixed(2)} || r > ${R_OUT.toFixed(2)}) return vec3(0.0);
    float ang = atan(P.y, P.x);
    float n = mix(turb(r, ang, uPhaseA, fp), turb(r, ang, uPhaseB, fp), uMixAB);
    // bord interne NET (dernière orbite stable, à la taille du pixel), bord externe qui s'efface
    float fpe = clamp(fp, 0.004, 0.3);
    float edge = smoothstep(${R_IN.toFixed(1)} - fpe, ${R_IN.toFixed(1)} + fpe, r) * (1.0 - smoothstep(${(R_OUT * 0.68).toFixed(2)}, ${R_OUT.toFixed(2)}, r));
    float T = pow(${R_IN.toFixed(1)} / r, 0.75);
    // Doppler et décalage gravitationnel
    float beta = sqrt(0.5 / (r - 1.0));
    vec3 vel = vec3(-sin(ang), cos(ang), 0.0);
    float g = sqrt(1.0 - beta * beta) / (1.0 - beta * dot(vel, k)) * sqrt(1.0 - 1.0 / r);
    if (uDoppler < 0.5) g = 0.80;
    vec3 col = heat(T * g * 1.18);
    // un disque mince mais pas infiniment : vu en RASANT, le trajet dans la matière s'allonge et
    // il brille davantage (bande avant vue à 10° ; l'arc lentillé, traversé plus à pic, garde sa valeur)
    float limb = clamp(pow(0.3 / max(abs(k.z), 0.05), 0.6), 0.8, 2.0);
    float I = pow(${R_IN.toFixed(1)} / r, 2.0) * g * g * g * (0.45 + 1.1 * n * n) * edge * limb * uBright;
    alpha = clamp(0.85 * (0.55 + 0.6 * n) * edge, 0.0, 0.92);
    return col * I * 2.2;
  }

  void main() {
    vec2 ndc = vUv * 2.0 - 1.0;
    vec3 d = normalize(uFwd + ndc.x * uTan.x * uRight + ndc.y * uTan.y * uUp);
    float D = length(uCam);
    vec3 e1 = uCam / D;
    float ce = -dot(d, e1);                       // cos ψ (ψ : angle au rayon entrant)
    vec3 perp = d + ce * e1;
    float sp = length(perp);
    vec3 e2 = sp > 1e-6 ? perp / sp : normalize(cross(e1, vec3(0.0, 0.0, 1.0)));
    float b = D * sp;
    // les rayons qui frôlent la sphère de photons tournent plusieurs fois avant de
    // s'échapper : ils ont ${extra} pas de plus, et recueillent le disque à chaque traversée
    bool nearCrit = b > ${B_CRIT.toFixed(5)} && b < ${(B_CRIT + CRIT_BAND).toFixed(5)};
    float u = 1.0 / D, w = u * ce / max(sp, 1e-6), phi = 0.0, phiP = -1.0;
    float s0 = e1.z;
    vec3 acc = vec3(0.0);
    float trans = 1.0;
    float order = 0.0;
    bool capt = false, esc = false;
    float phiE = 0.0;
    const float H0 = ${dphi.toFixed(5)};
    for (int i = 0; i < ${steps + extra}; i++) {
      if (i >= ${steps} && !nearCrit) break;
      // le pas : plus fin près de r = 1,5 (u = 2/3), là où l'orbite est instable
      float H = H0 * (0.45 + 0.55 * smoothstep(0.04, 0.22, abs(u - 0.666667)));
      float k1u = w, k1w = -u + 1.5 * u * u;
      float u2 = u + 0.5 * H * k1u; float k2u = w + 0.5 * H * k1w, k2w = -u2 + 1.5 * u2 * u2;
      float u3 = u + 0.5 * H * k2u; float k3u = w + 0.5 * H * k2w, k3w = -u3 + 1.5 * u3 * u3;
      float u4 = u + H * k3u; float k4u = w + H * k3w, k4w = -u4 + 1.5 * u4 * u4;
      float un = u + H / 6.0 * (k1u + 2.0 * k2u + 2.0 * k3u + k4u);
      float wn = w + H / 6.0 * (k1w + 2.0 * k2w + 2.0 * k3w + k4w);
      float phin = phi + H;
      if (phiP < 0.0 && w > 0.0 && wn <= 0.0) phiP = phi + H * w / (w - wn);
      // le plan du disque traversé dans ce pas ?
      float s1 = cos(phin) * e1.z + sin(phin) * e2.z;
      if (s0 * s1 < 0.0 && trans > 0.01) {
        float t = s0 / (s0 - s1);
        float pc = phi + t * H, uc = mix(u, max(un, 0.0), t), wc = mix(w, wn, t);
        if (uc > ${(0.9 / R_OUT).toFixed(4)}) {
          float r = 1.0 / uc;
          vec3 rh = cos(pc) * e1 + sin(pc) * e2, ph = -sin(pc) * e1 + cos(pc) * e2;
          vec3 P = r * rh;
          vec3 T = normalize((-wc / (uc * uc)) * rh + r * ph);
          // la taille du pixel dans le plan du disque : distance x angle du pixel / incidence,
          // plus grande pour les images d'ordre supérieur (comprimées par la lentille)
          float fp = D * uPix / max(abs(T.z), 0.06) * (1.0 + 3.0 * order);
          float a;
          vec3 c = disk(P, -T, fp, a);
          // la traversée émet à PLEINE intensité ; son opacité n'atténue que ce qui est derrière
          acc += trans * c;
          trans *= 1.0 - a;
          order += 1.0;
        }
      }
      if (un <= 0.0) { esc = true; phiE = phi + H * u / (u - un); break; }
      if (un >= 1.0) { capt = true; break; }
      u = un; w = wn; phi = phin; s0 = s1;
    }
    if (!esc && !capt) {
      if (b < ${B_CRIT.toFixed(5)}) capt = true;
      else { esc = true; phiE = phiP >= 0.0 ? 2.0 * phiP + asin(min(sp, 1.0)) : phi; }
    }
    vec3 ex = cos(phiE) * e1 + sin(phiE) * e2;
    oDisk = vec4(acc, capt ? 0.0 : trans);
    oDir = vec4(capt ? vec3(0.0) : ex - d, capt ? 1.0 : 0.0);   // w : capturé (sert à l'interpolation masquée)
  }
  `;
}

const COMPOSE_VERTEX = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
const COMPOSE_FRAGMENT = `
  precision highp float;
  uniform sampler2D tDisk;
  uniform sampler2D tDir;
  uniform vec3 uCam;
  uniform vec3 uRight, uUp, uFwd;
  uniform vec2 uTan;
  uniform vec2 uRes;
  uniform vec2 uLowRes;            // la taille de la cible basse
  uniform float uBright;
  uniform float uExposure;
  uniform float uSafeAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;

  float h1(vec3 p) { p = fract(p * 0.1031); p += dot(p, p.zyx + 31.32); return fract((p.x + p.y) * p.z); }
  vec3 h3(vec3 p) { p = fract(p * vec3(0.1031, 0.1030, 0.0973)); p += dot(p, p.yxz + 33.33); return fract((p.xxy + p.yxx) * p.zyx); }
  float vnoise3(vec3 p) {
    vec3 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
    return mix(mix(mix(h1(i), h1(i + vec3(1, 0, 0)), f.x), mix(h1(i + vec3(0, 1, 0)), h1(i + vec3(1, 1, 0)), f.x), f.y),
               mix(mix(h1(i + vec3(0, 0, 1)), h1(i + vec3(1, 0, 1)), f.x), mix(h1(i + vec3(0, 1, 1)), h1(i + vec3(1, 1, 1)), f.x), f.y), f.z);
  }
  // le ciel : des étoiles d'un pixel sur les six faces d'un cube, une voie lactée très douce
  const float G = 220.0;
  vec2 cubeUv(vec3 dir, out float face) {
    vec3 a = abs(dir);
    if (a.x >= a.y && a.x >= a.z) { face = dir.x > 0.0 ? 0.0 : 1.0; return dir.yz / a.x; }
    if (a.y >= a.z) { face = dir.y > 0.0 ? 2.0 : 3.0; return dir.xz / a.y; }
    face = dir.z > 0.0 ? 4.0 : 5.0; return dir.xy / a.z;
  }
  // d0 : la direction sans lentille, pour la démagnification (une région du ciel
  // comprimée par la lentille montre ses étoiles plus faibles : sinon un nuage
  // d'étoiles s'entasse autour de l'anneau)
  vec3 sky(vec3 dir, vec3 d0) {
    float face, face0;
    vec2 uv = cubeUv(dir, face);
    vec2 g = (uv * 0.5 + 0.5) * G;
    vec2 g0 = (cubeUv(d0, face0) * 0.5 + 0.5) * G;
    vec2 j0x = dFdx(g0), j0y = dFdy(g0);
    float det0 = abs(j0x.x * j0y.y - j0x.y * j0y.x);
    vec2 cell = floor(g);
    vec3 h = h3(vec3(cell, face * 17.0 + 3.0));
    vec3 col = vec3(0.0);
    if (h.x < 0.13) {
      vec2 sp = cell + 0.2 + 0.6 * h.yz;
      // l'écart à l'étoile ramené en PIXELS par l'inverse du jacobien écran -> ciel :
      // un point d'un pixel (R7) ; étirée par la lentille, l'étoile devient un arc net
      // (une division composante par composante l'étalait en diagonale)
      vec2 jx = dFdx(g), jy = dFdy(g);
      float det = jx.x * jy.y - jx.y * jy.x;
      vec2 q = g - sp;
      vec2 dpx = abs(det) > 1e-9 ? vec2(jy.y * q.x - jy.x * q.y, -jx.y * q.x + jx.x * q.y) / det : vec2(9.0);
      float m = pow(h.x / 0.13, 5.0);
      // (démagnification adoucie : la compression de l'anneau d'Einstein reste visible,
      // un amas d'étoiles concentrées en anneau juste hors du disque)
      float mu = clamp(det0 / max(abs(det), 1e-12), 0.0, 1.0);
      float I = (0.10 + 1.8 * m) * exp(-dot(dpx, dpx) * 1.1) * pow(mu, 0.3);
      vec3 tint = mix(vec3(1.0, 0.85, 0.7), vec3(0.75, 0.85, 1.0), fract(h.y * 7.31));
      col += tint * I;
    }
    vec3 nG = normalize(vec3(0.35, 0.80, 0.48));
    float band = exp(-pow(dot(dir, nG) / 0.20, 2.0));
    col += vec3(0.55, 0.60, 0.80) * band * (0.35 + 0.65 * vnoise3(dir * 5.0)) * 0.035;
    return col;
  }
  float safeAtt(vec2 uv) {
    vec2 ndc = uv * 2.0 - 1.0;
    vec2 s = vec2(ndc.x * uSafeAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
  void main() {
    vec2 ndc = vUv * 2.0 - 1.0;
    vec3 d = normalize(uFwd + ndc.x * uTan.x * uRight + ndc.y * uTan.y * uUp);
    float D = length(uCam);
    vec3 e1 = uCam / D;
    float ce = -dot(d, e1);
    float b = D * sqrt(max(0.0, 1.0 - ce * ce));
    // un pixel, en unités de b (b = D sin ψ, ψ varie d'un angle de pixel par pixel)
    float wb = D * 2.0 * uTan.y / uRes.y;
    // l'ombre, nette en pleine résolution (b exact)
    float lit = smoothstep(${B_CRIT.toFixed(5)} - 0.6 * wb, ${B_CRIT.toFixed(5)} + 0.6 * wb, b);
    // L'INTERPOLATION MASQUÉE : quatre texels de la cible basse, et seulement ceux du même
    // côté de l'horizon que le pixel (capturés dans l'ombre, échappés dehors) - sans quoi
    // la lumière de l'anneau débordait en gris dans l'ombre (tier 1 : tiers de résolution)
    vec2 st = vUv * uLowRes - 0.5, i0 = floor(st), fr = st - i0;
    vec4 dk = vec4(0.0); vec3 dl = vec3(0.0); float wsum = 0.0;
    for (int k = 0; k < 4; k++) {
      vec2 o = vec2(mod(float(k), 2.0), floor(float(k) / 2.0));
      vec2 uvk = (i0 + o + 0.5) / uLowRes;
      vec4 tdir = texture2D(tDir, uvk);
      float wk = (o.x > 0.5 ? fr.x : 1.0 - fr.x) * (o.y > 0.5 ? fr.y : 1.0 - fr.y);
      wk *= lit > 0.5 ? 1.0 - tdir.w : tdir.w;
      dk += wk * texture2D(tDisk, uvk); dl += wk * tdir.xyz; wsum += wk;
    }
    if (wsum > 1e-4) { dk /= wsum; dl /= wsum; } else { dk = texture2D(tDisk, vUv); dl = texture2D(tDir, vUv).xyz; }
    if (lit < 0.5) dk.a = 0.0;
    vec3 dir = normalize(d + dl);
    // collé au bord de l'ombre, les images d'ordre supérieur du disque sont plus fines qu'un
    // texel de la cible basse (des perles) : l'anneau analytique en pleine résolution les remplace
    float dbr = max(b - ${B_CRIT.toFixed(5)}, 0.0);
    dk.rgb *= lit > 0.5 ? smoothstep(2.0 * wb, 6.0 * wb, dbr) : 1.0;
    float trans = max(dk.a, 0.0);
    // l'anneau de photons : 1 à 2 px juste hors de l'ombre (R1)
    // L'ANNEAU DE PHOTONS : 1 à 3 px, blanc chaud, très lumineux (au-delà de 0,9 après la
    // courbe), collé au bord de l'ombre ; une lueur douce vers l'EXTÉRIEUR seulement
    // (le bloom partagé, qui débordait en gris dans l'ombre, est coupé)
    float db = max(b - ${B_CRIT.toFixed(5)}, 0.0);
    float ring = exp(-db / (0.6 * wb)) * lit;
    float glow = exp(-db / (9.0 * wb)) * lit;
    vec3 col = dk.rgb + trans * lit * sky(dir, d) + trans * (ring * 6.0 + glow * 0.18) * vec3(1.0, 0.86, 0.66) * uBright;
    col = 1.0 - exp(-col * uExposure);
    // R3 : le masque de pochette MULTIPLIE ; l'ombre (capturée, sans disque devant) reste (0, 0, 0)
    col *= safeAtt(vUv);
    gl_FragColor = vec4(col, 1.0);
  }
`;

const TRACE_MATERIALS = new Map();

// =============================================================================

export class BlackholeVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._layout = TIER[this._tier] || TIER[1];
    const st = parseInt(urlParam('bhsteps'), 10);
    this._steps = Number.isFinite(st) && st >= 8 ? Math.min(256, st) : this._layout.steps;
    this._extra = this._layout.extra;
    this._doppler = urlParam('bhdoppler') === '0' ? 0 : 1;
    const bt = parseFloat(urlParam('bht'));
    this._forceT = Number.isFinite(bt) && bt >= 0 ? bt : null;
    // pas de look VDJ : un bloom léger pour l'anneau et le bord intérieur, aucun grain fort
    // bloom partagé COUPÉ : il étalait un dégradé gris dans l'ombre (R3) ; l'anneau a sa propre lueur
    this._post = { bloomStrength: 0, bloomThreshold: 1.0, grain: 0.008, vignette: 0.35, aberration: 0, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 };
    if (urlParam('bhprobe') === '1' && typeof window !== 'undefined') window.__blackhole = this;
  }

  get postProcessing() { return this._post; }

  init(scene) {
    this._scene = scene;
    const key = this._steps + ':' + this._extra;
    let mat = TRACE_MATERIALS.get(key);
    if (!mat) {
      mat = new THREE.RawShaderMaterial({
        glslVersion: THREE.GLSL3,
        uniforms: {
          uCam: { value: new THREE.Vector3() }, uRight: { value: new THREE.Vector3() }, uUp: { value: new THREE.Vector3() }, uFwd: { value: new THREE.Vector3() },
          uTan: { value: new THREE.Vector2() }, uPix: { value: 0.001 }, uPhaseA: { value: 0 }, uPhaseB: { value: 0 }, uMixAB: { value: 0 }, uBright: { value: 1 }, uDoppler: { value: 1 }
        },
        vertexShader: TRACE_VERTEX, fragmentShader: buildTraceFragment(this._steps, stepAngle(this._steps), this._extra), depthTest: false, depthWrite: false
      });
      TRACE_MATERIALS.set(key, mat);
    }
    this._tmat = mat; this._tu = mat.uniforms;
    this._target = new THREE.WebGLRenderTarget(16, 16, { count: 2, type: THREE.HalfFloatType, format: THREE.RGBAFormat, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, depthBuffer: false, stencilBuffer: false, generateMipmaps: false });
    this._tScene = new THREE.Scene();
    this._tQuad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mat);
    this._tQuad.frustumCulled = false;
    this._tScene.add(this._tQuad);
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10);
    this._ortho.position.z = 1;
    // la composition, dans la scène partagée : les mêmes vecteurs de caméra que la passe d'intégration
    this._cu = {
      tDisk: { value: this._target.textures[0] }, tDir: { value: this._target.textures[1] },
      uCam: this._tu.uCam, uRight: this._tu.uRight, uUp: this._tu.uUp, uFwd: this._tu.uFwd, uTan: this._tu.uTan,
      uRes: { value: new THREE.Vector2(1280, 720) }, uLowRes: { value: new THREE.Vector2(640, 360) }, uBright: this._tu.uBright, uExposure: { value: EXPOSURE },
      uSafeAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._cu, vertexShader: COMPOSE_VERTEX, fragmentShader: COMPOSE_FRAGMENT, depthTest: false, depthWrite: false }));
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    this._display.onBeforeRender = (renderer) => this._render(renderer);
    scene.add(this._display);
    this._size = new THREE.Vector2();

    this._adapt = new Adapt(); this._a = this._adapt.get();
    // l'horloge de scène partagée : la scène commence au début de l'approche (déjà proche : cadrage serré)
    this._clock = new SceneClock({ period: APPROACH_S + HOLD_S + DRIFT_S, start: 0 });
    this._sceneT = 0;
    this._phase = 0; this._I = null; this._paused = false; this._rendered = false;
    this._tu.uDoppler.value = this._doppler;
    this._writeCamera(this._forceT != null ? this._forceT : 0);
  }

  _writeCamera(s) {
    const D = cameraDistance(s);
    this._D = D;
    const cam = new THREE.Vector3(0, -D * Math.cos(ELEVATION), D * Math.sin(ELEVATION));
    const fwd = cam.clone().multiplyScalar(-1 / D);
    const right = new THREE.Vector3().crossVectors(fwd, new THREE.Vector3(0, 0, 1)).normalize();
    const up = new THREE.Vector3().crossVectors(right, fwd);
    const U = this._tu;
    U.uCam.value.copy(cam); U.uFwd.value.copy(fwd); U.uRight.value.copy(right); U.uUp.value.copy(up);
    const A = safeZoneUniforms.uAspect.value || 16 / 9;
    const ty = Math.tan(VFOV / 2);
    U.uTan.value.set(ty * A, ty);
  }

  update(deltaTime, profile) {
    this._paused = profile.playbackState != null && profile.playbackState !== 'PLAYING';
    const dt = this._paused ? 0 : Math.min(Math.max(deltaTime, 0), 0.1);
    const a = this._a = this._adapt.update(dt, profile);
    this._clock.update(dt);
    if (this._paused) { this._writeCamera(this._sceneT); return; }   // pause : gel (le rendu refait la même image)
    this._sceneT = this._forceT != null ? this._forceT : this._clock.elapsed;
    this._writeCamera(this._sceneT);
    // l'intensité, sur plusieurs mesures : luminosité et vitesse du disque (aucune impulsion, R9)
    const I = a.intensity;
    this._I = this._I == null ? I : this._I + (I - this._I) * (1 - Math.exp(-dt / 8));
    const rate = 0.16 * (0.55 + 0.9 * this._I);           // rad/s au bord intérieur
    this._phase += dt * rate;
    const P = PHASE_PERIOD;
    const tA = this._phase / P - Math.floor(this._phase / P), tB = tA + 0.5 - Math.floor(tA + 0.5);
    const U = this._tu;
    U.uPhaseA.value = tA * P; U.uPhaseB.value = tB * P;
    U.uMixAB.value = 1 - Math.abs(2 * tB - 1);              // poids de la couche B : 0 quand elle se recale
    U.uBright.value = 0.70 + 0.45 * this._I;
  }

  _render(renderer) {
    const prev = renderer.getRenderTarget();
    if (prev) this._size.set(prev.width, prev.height); else renderer.getDrawingBufferSize(this._size);
    const s = this._layout.scale;
    const w = Math.max(2, Math.round(this._size.x * s)), h = Math.max(2, Math.round(this._size.y * s));
    if (this._target.width !== w || this._target.height !== h) this._target.setSize(w, h);
    this._tu.uPix.value = 2 * this._tu.uTan.value.y / h;
    this._cu.uLowRes.value.set(w, h);
    this._cu.uRes.value.copy(this._size);
    if (!this._paused || !this._rendered) {
      renderer.setRenderTarget(this._target);
      renderer.render(this._tScene, this._ortho);
      this._rendered = true;
    }
    renderer.setRenderTarget(prev);
  }

  getProbe() {
    return { tier: this._tier, steps: this._steps, sceneT: +this._sceneT.toFixed(2), D: +this._D.toFixed(2), phase: +this._phase.toFixed(3), bright: +this._tu.uBright.value.toFixed(3),
      intensity: +this._a.intensity.toFixed(3), doppler: this._doppler, sim: this._adapt.simulated };
  }

  dispose() {
    if (this._scene && this._display) { this._display.onBeforeRender = null; this._scene.remove(this._display); this._display.geometry.dispose(); this._display.material.dispose(); }
    if (this._tQuad) this._tQuad.geometry.dispose();
    if (this._target) this._target.dispose();
    if (this._adapt) this._adapt.dispose();
    if (typeof window !== 'undefined' && window.__blackhole === this) window.__blackhole = null;
    this._scene = null;
  }
}
